import { useQuery } from '@apollo/client';
import { Disclosure } from '@headlessui/react';
import {
  ArrowTopRightOnSquareIcon,
  BuildingOfficeIcon,
  MinusIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import classNames from 'classnames';
import posthog from 'posthog-js';
import { FC, useEffect, useState } from 'react';

import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

import alIndiaLogo from 'media/alIndiaLogoGrey.png';

import constants from 'utils/constants';
import { toTitleCase } from 'utils/format-helper';

import { discoverDealsFaqs } from './DiscoverDealsFAQs';

const DISCOVER_DEALS_QUERY = gql(`
  query DiscoverDeals {
    discoverDeals {
      id
      publicData {
        name
        description
        leads
        round
        founders
        dealInviteLink
      }
      companyInformation {
        company {
          image
          website
        }
        industries
      }
      syndicate {
        name
        image
      }
    }
  }
`);

interface DiscoverDealProps {
  deal: {
    id: string;
    publicData?: {
      name?: string | null;
      description?: string | null;
      leads?: string | null;
      round?: string | null;
      founders?: string | null;
      dealInviteLink?: string | null;
    } | null;
    companyInformation: {
      company: {
        image?: string | null;
        website?: string | null;
      };
      industries?: ReadonlyArray<string | null> | null;
    };
    syndicate: {
      name: string;
      image?: string | null;
    };
  };
}

const DiscoverDealCard: FC<DiscoverDealProps> = ({ deal }) => {
  const [logoError, setLogoError] = useState(false);
  const industries = (deal.companyInformation.industries ?? []).filter(
    (s): s is string => s !== null
  );
  const hasLogo = deal.companyInformation.company.image && !logoError;
  const displayedIndustries = industries.slice(0, 3);
  const extraIndustries = industries.length - 3;

  return (
    <article className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col gap-4 shadow-[0_1px_2px_rgba(16,24,40,.05),0_10px_28px_-14px_rgba(16,24,40,.18)] hover:-translate-y-0.5 hover:shadow-[0_1px_2px_rgba(16,24,40,.05),0_22px_48px_-20px_rgba(16,24,40,.28)] hover:border-slate-300 transition-all duration-200">
      {/* Header: logo + name + website link */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-11 h-11 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
          {hasLogo ? (
            <img
              src={deal.companyInformation.company.image || constants.ALT_AVATAR_URL}
              alt={deal.publicData?.name || 'Company'}
              className="w-full h-full object-contain p-1"
              onError={() => setLogoError(true)}
            />
          ) : (
            <BuildingOfficeIcon className="w-5 h-5 text-slate-400" />
          )}
        </div>
        <h3 className="flex-1 mt-0.5 text-[17px] font-bold leading-snug text-slate-900">
          {deal.publicData?.name || 'Deal'}
        </h3>
        {deal.companyInformation.company.website && (
          <button
            className="flex-shrink-0 w-9 h-9 rounded-[10px] border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-colors"
            onClick={() => window.open(deal.companyInformation.company.website || '', '_blank')}
            aria-label="Open company website"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Description */}
      <p className="text-[13.5px] leading-relaxed text-slate-600 line-clamp-3">
        {deal.publicData?.description || 'No description'}
      </p>

      {/* Sector tags */}
      {displayedIndustries.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {displayedIndustries.map((s, idx) => (
            <span
              key={idx}
              className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full"
            >
              {toTitleCase(s)}
            </span>
          ))}
          {extraIndustries > 0 && (
            <span className="px-2.5 py-1 bg-slate-100 text-slate-500 text-xs font-semibold rounded-full">
              +{extraIndustries}
            </span>
          )}
        </div>
      )}

      {/* Lead investor */}
      {deal.publicData?.leads && (
        <>
          <hr className="border-slate-100" />
          <div className="space-y-2">
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">
              Lead investor
            </p>
            <span className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-2.5 py-1 text-sm font-medium text-slate-800">
              <span className="w-4 h-4 rounded-full bg-indigo-500 flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0">
                {deal.publicData.leads.charAt(0).toUpperCase()}
              </span>
              {deal.publicData.leads}
              <span className="text-[10px] font-bold uppercase tracking-wide text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
                Lead
              </span>
            </span>
          </div>
        </>
      )}

      {/* Deal terms */}
      {(deal.publicData?.round || deal.publicData?.founders) && (
        <>
          <hr className="border-slate-100" />
          <div className="divide-y divide-dashed divide-slate-100">
            {deal.publicData?.round && (
              <div className="flex items-baseline justify-between py-2">
                <span className="text-sm text-slate-500">Round</span>
                <span className="text-sm font-semibold text-slate-900">
                  {deal.publicData.round}
                </span>
              </div>
            )}
            {deal.publicData?.founders && (
              <div className="flex items-baseline justify-between py-2 gap-4">
                <span className="text-sm text-slate-500 shrink-0">Founders</span>
                <span className="text-sm font-semibold text-slate-900 text-right line-clamp-1">
                  {deal.publicData.founders}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Footer */}
      <div className="mt-auto flex items-center">
        <button
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
          onClick={() => window.open(deal.publicData?.dealInviteLink || '', '_blank')}
        >
          View deal
          <svg
            className="w-3.5 h-3.5"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 8h10M9 4l4 4-4 4" />
          </svg>
        </button>
      </div>
    </article>
  );
};

const DiscoverDeals: FC = () => {
  const { loading, error, data, refetch } = useQuery(DISCOVER_DEALS_QUERY);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    const utmContent = urlParams.get('utm_content');
    if (utmSource || utmContent) {
      posthog.capture('discover_deals_page_viewed', {
        utm_source: utmSource || null,
        utm_content: utmContent || null,
      });
    }
  }, []);

  if (loading) return <LoadingIndicator />;
  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;
  if (!data.discoverDeals) return <ErrorMessage title="No deals found" refetch={refetch} />;

  const deals = data.discoverDeals;

  return (
    <div>
      <CoverImage />

      <div className="px-8 pb-24 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {deals.map(deal => (
            <DiscoverDealCard key={deal.id} deal={deal} />
          ))}
        </div>
      </div>

      {/* FAQs */}
      <div
        id="faqs"
        className="mt-6 my-20 max-w-4xl mx-auto p-5 w-full divide-y divide-gray-900/10 bg-gray-50 rounded-xl"
      >
        <h2 className="text-2xl font-semibold leading-10 tracking-tight text-gray-800">
          Frequently asked questions
        </h2>
        <dl className="mt-5 space-y-6 divide-y divide-gray-900/10">
          <Disclosure as="div" className="pt-6" defaultOpen={true}>
            {({ open }) => (
              <>
                <dt>
                  <Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-700">
                    <span
                      className={classNames('text-lg font-semibold leading-7', open && 'underline')}
                    >
                      FAQs
                    </span>
                    <span className="ml-6 flex h-7 items-center">
                      {open ? (
                        <MinusIcon className="h-6 w-6" aria-hidden="true" />
                      ) : (
                        <PlusIcon className="h-6 w-6" aria-hidden="true" />
                      )}
                    </span>
                  </Disclosure.Button>
                </dt>
                <Disclosure.Panel as="dd" className="mt-2 pr-12">
                  {discoverDealsFaqs.map(faq => (
                    <Disclosure as="div" key={faq.question} className="pt-6">
                      {({ open }) => (
                        <>
                          <dt>
                            <Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-700">
                              <span className="text-base font-semibold leading-7">
                                {faq.question}
                              </span>
                              <span className="ml-6 flex h-7 items-center">
                                {open ? (
                                  <MinusIcon className="h-6 w-6" aria-hidden="true" />
                                ) : (
                                  <PlusIcon className="h-6 w-6" aria-hidden="true" />
                                )}
                              </span>
                            </Disclosure.Button>
                          </dt>
                          <Disclosure.Panel as="dd" className="mt-2 pr-12">
                            <div className="text-base leading-7 text-gray-600">{faq.answer}</div>
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ))}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </dl>
      </div>
    </div>
  );
};

const CoverImage: FC = () => {
  return (
    <div className="bg-white mb-10 relative bg-no-repeat bg-[length:500px_500px] bg-[120%_5rem]">
      <div className="px-6 pb-10 pt-8">
        <img
          src={alIndiaLogo}
          alt="Discover Deals"
          className="mx-auto w-[20rem] h-auto object-contain p-4 rounded-md mb-8"
        />
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-xl font-bold tracking-tight sm:text-5xl">Discover Deals</h2>
          <div className="mt-6">
            <h3 className="text-lg inline">New to AngelList India? </h3>
            <h3
              className="text-lg inline hover:cursor-pointer text-indigo-600"
              onClick={() => {
                const el = document.getElementById('faqs');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              View our FAQs
            </h3>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscoverDeals;
